import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Form2 from './Form2';
import GratuityNomination from './GratuityNomination';
import Form11 from './Form11';
import FormL from './FormL';
import Review from './Review';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import './MainPage.css';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import FooterSection from '../../shared-components/footerSection';
import HeaderSection from '../../shared-components/headerSection';
import { FormData, NomineeData, TabType, FormValidateHandle } from './typesIN';
import { empProfile, empProfileDetails } from '../Profile/ProfileSlice';
import {
    mapProfileToDefaults,
    buildForm2InitialData,
    buildGratuityInitialData,
    buildForm11InitialData,
    buildFormLInitialData,
    isFemaleEmployee,
    FieldEditability,
} from './profileFormMapper';

interface MainPageProps {
    oracleId?: string;
    stepId?: string;
    employeeUid?: string;
    processEmployeeId?: string;
}

const MainPage: React.FC<MainPageProps> = ({ oracleId, stepId, employeeUid, processEmployeeId }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { profileData } = useSelector(empProfileDetails);

    const [activeTab, setActiveTab] = useState<TabType>('form2');
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [profileLoaded, setProfileLoaded] = useState(false);

    // ── Derive female employee status from profileData ───────────────────────
    // isFemale drives Form L tab visibility — recalculated once profile loads
    const [isFemale, setIsFemale] = useState(false);

    // ── Tab order — dynamically includes formL for female employees ──────────
    const TAB_ORDER: TabType[] = isFemale
        ? ['form2', 'gratuity', 'form11', 'formL', 'review']
        : ['form2', 'gratuity', 'form11', 'review'];

    const [completedForms, setCompletedForms] = useState({
        form2: false,
        gratuity: false,
        form11: false,
        formL: false,   // only relevant for female employees
    });

    const [form2Data, setForm2Data] = useState<FormData>({});
    const [gratuityData, setGratuityData] = useState<FormData>({});
    const [form11Data, setForm11Data] = useState<FormData>({});
    const [formLData, setFormLData] = useState<FormData>({});
    const [form2Nominees, setForm2Nominees] = useState<NomineeData[]>([]);
    const [gratuityNominees, setGratuityNominees] = useState<NomineeData[]>([]);
    const [fieldEditability, setFieldEditability] = useState<FieldEditability>({});
    const [autoSignature, setAutoSignature] = useState<string>('');

    // ── Refs for triggering validation from outside each form ────────────────
    const form2Ref = useRef<FormValidateHandle>(null);
    const gratuityRef = useRef<FormValidateHandle>(null);
    const form11Ref = useRef<FormValidateHandle>(null);
    const formLRef = useRef<FormValidateHandle>(null);

    // ── IDs ──────────────────────────────────────────────────────────────────
    const storedOracleId = oracleId || localStorage.getItem('digitalFormOracleId') || '';
    const storedStepId = stepId || localStorage.getItem('digitalFormStepId') || '';
    const storedEmployeeUid = employeeUid || localStorage.getItem('digitalFormEmployeeUid') || '';
    const storedProcessEmployeeId = processEmployeeId || localStorage.getItem('digitalFormProcessEmployeeId') || '';

    useEffect(() => {
        if (storedOracleId) localStorage.setItem('digitalFormOracleId', storedOracleId);
        if (storedStepId) localStorage.setItem('digitalFormStepId', storedStepId);
        if (storedEmployeeUid) localStorage.setItem('digitalFormEmployeeUid', storedEmployeeUid);
        if (storedProcessEmployeeId) localStorage.setItem('digitalFormProcessEmployeeId', storedProcessEmployeeId);
    }, [storedOracleId, storedStepId, storedEmployeeUid, storedProcessEmployeeId]);

    useEffect(() => {
        if (storedOracleId && !profileData) {
            dispatch<any>(empProfile({ action: 'getEmpDetails', oracleId: storedOracleId, astt: 'N' }));
        }
    }, [storedOracleId, profileData, dispatch]);

    useEffect(() => {
        if (profileData && !profileLoaded) {
            const { defaults, editability } = mapProfileToDefaults(profileData);
            setFieldEditability(editability);

            // ── Detect gender for Form L ─────────────────────────────────────
            const femaleFlag = isFemaleEmployee(profileData);
            setIsFemale(femaleFlag);

            const profileForm2 = buildForm2InitialData(defaults);
            const profileGratuity = buildGratuityInitialData(defaults);
            const profileForm11 = buildForm11InitialData(defaults);

            const mergeDefaults = (prev: FormData, defs: FormData): FormData => {
                const merged = { ...prev };
                Object.entries(defs).forEach(([key, val]) => {
                    const existing = prev[key];
                    if (val !== undefined && val !== null && val !== '' &&
                        (existing === undefined || existing === null || existing === '')) {
                        merged[key] = val;
                    }
                });
                return merged;
            };

            setForm2Data(prev => mergeDefaults(prev, profileForm2));
            setGratuityData(prev => mergeDefaults(prev, profileGratuity));
            setForm11Data(prev => mergeDefaults(prev, profileForm11));

            // ── Form L defaults — only populate if female ────────────────────
            if (femaleFlag) {
                const profileFormL = buildFormLInitialData(defaults);
                setFormLData(prev => mergeDefaults(prev, profileFormL));
            }

            setProfileLoaded(true);
        }
    }, [profileData, profileLoaded]);

    useEffect(() => {
        const hasSeenWarning = localStorage.getItem('hasSeenDigitalFormWarning');
        if (!hasSeenWarning) setShowWarningDialog(true);
    }, []);

    const handleWarningContinue = () => {
        localStorage.setItem('hasSeenDigitalFormWarning', 'true');
        setShowWarningDialog(false);
    };

    const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    // ── Draft save callbacks ─────────────────────────────────────────────────
    const handleForm2Draft = useCallback((data: FormData, nominees: NomineeData[]) => {
        setForm2Data(data);
        setForm2Nominees(nominees);
    }, []);

    const handleGratuityDraft = useCallback((data: FormData, nominees: NomineeData[]) => {
        setGratuityData(data);
        setGratuityNominees(nominees);
    }, []);

    const handleForm11Draft = useCallback((data: FormData) => {
        setForm11Data(data);
    }, []);

    const handleFormLDraft = useCallback((data: FormData) => {
        setFormLData(data);
    }, []);

    // ── Tab navigation — validate current tab before going forward ───────────
    const handleTabClick = (tab: TabType) => {
        const currentIndex = TAB_ORDER.indexOf(activeTab);
        const targetIndex = TAB_ORDER.indexOf(tab);

        // Backward navigation — always free
        if (targetIndex <= currentIndex) {
            setActiveTab(tab);
            scrollTop();
            return;
        }

        if (currentIndex <= TAB_ORDER.indexOf('form2') && targetIndex > TAB_ORDER.indexOf('form2')) {
            const isValid = form2Ref.current?.validate() ?? false;
            if (!isValid) { scrollTop(); return; }
            setCompletedForms(prev => ({ ...prev, form2: true }));
        }

        if (currentIndex <= TAB_ORDER.indexOf('gratuity') && targetIndex > TAB_ORDER.indexOf('gratuity')) {
            if (!completedForms.form2) { setActiveTab('form2'); scrollTop(); return; }
            const isValid = gratuityRef.current?.validate() ?? false;
            if (!isValid) { setActiveTab('gratuity'); scrollTop(); return; }
            setCompletedForms(prev => ({ ...prev, gratuity: true }));
        }

        if (currentIndex <= TAB_ORDER.indexOf('form11') && targetIndex > TAB_ORDER.indexOf('form11')) {
            if (!completedForms.gratuity) { setActiveTab('gratuity'); scrollTop(); return; }
            const isValid = form11Ref.current?.validate() ?? false;
            if (!isValid) { setActiveTab('form11'); scrollTop(); return; }
            setCompletedForms(prev => ({ ...prev, form11: true }));
        }

        // Form L gate — only present for female employees
        if (isFemale && TAB_ORDER.includes('formL')) {
            if (currentIndex <= TAB_ORDER.indexOf('formL') && targetIndex > TAB_ORDER.indexOf('formL')) {
                if (!completedForms.form11) { setActiveTab('form11'); scrollTop(); return; }
                const isValid = formLRef.current?.validate() ?? false;
                if (!isValid) { setActiveTab('formL'); scrollTop(); return; }
                setCompletedForms(prev => ({ ...prev, formL: true }));
            }
        }

        setActiveTab(tab);
        scrollTop();
    };

    // ── Continue handlers ────────────────────────────────────────────────────
    const handleForm2Continue = (data: FormData, nominees: NomineeData[]) => {
        setForm2Data(data);
        setForm2Nominees(nominees);
        setAutoSignature((data.subscriberSignature as string) || '');
        setGratuityData(prev => ({
            ...prev,
            employeeName: prev.employeeName || data.fullName || '',
            gender: prev.gender || data.gender || '',
            maritalStatus: prev.maritalStatus || data.maritalStatus || '',
            permanentAddress: prev.permanentAddress || data.permanentAddress || '',
            nomineeName: prev.nomineeName || data.nomineeName || '',
            nomineeAddress: prev.nomineeAddress || data.nomineeAddress || '',
            relationship: prev.relationship || data.nomineeRelationship || '',
            nomineeDob: prev.nomineeDob || data.nomineeDob || undefined,
            proportion: prev.proportion || data.TotalAmount || '',
        }));
        setForm11Data(prev => ({
            ...prev,
            memberName: prev.memberName || data.fullName || '',
            fatherName: prev.fatherName || data.fatherName || '',
            dob: prev.dob || data.dob || undefined,
            gender: prev.gender || data.gender || '',
            maritalStatus: prev.maritalStatus || data.maritalStatus || '',
            uan: prev.uan || data.uan || '',
        }));
        setCompletedForms(prev => ({ ...prev, form2: true }));
        setActiveTab('gratuity');
        scrollTop();
    };

    const handleGratuityContinue = (data: FormData, nominees: NomineeData[]) => {
        setGratuityData(data);
        setGratuityNominees(nominees);
        setCompletedForms(prev => ({ ...prev, gratuity: true }));
        setActiveTab('form11');
        scrollTop();
    };

    const handleForm11Submit = (data: FormData) => {
        setForm11Data(data);
        setCompletedForms(prev => ({ ...prev, form11: true }));
        // Female employees → go to Form L; others → go to review
        setActiveTab(isFemale ? 'formL' : 'review');
        scrollTop();
    };

    // ── NEW: Form L submit handler ───────────────────────────────────────────
    const handleFormLSubmit = (data: FormData) => {
        setFormLData(data);
        setCompletedForms(prev => ({ ...prev, formL: true }));
        setActiveTab('review');
        scrollTop();
    };

    const handleBackToForm2 = () => { setActiveTab('form2'); scrollTop(); };
    const handleBackToGratuity = () => { setActiveTab('gratuity'); scrollTop(); };
    const handleBackToForm11 = () => { setActiveTab('form11'); scrollTop(); };
    // ── Back from Form L goes to Form11 ─────────────────────────────────────
    const handleBackToFormL = () => { setActiveTab('formL'); scrollTop(); };

    const handleFinalSubmitSuccess = () => {
        setForm2Data({});
        setGratuityData({});
        setForm11Data({});
        setFormLData({});
        setForm2Nominees([]);
        setGratuityNominees([]);
        setCompletedForms({ form2: false, gratuity: false, form11: false, formL: false });
        setActiveTab('form2');
        setProfileLoaded(false);
        setIsFemale(false);
        localStorage.removeItem('hasSeenDigitalFormWarning');
        navigate('/');
    };

    return (
        <div className="app-container">
            <HeaderSection announcements={[]} />
            <main className="main-content">
                <div className="form-wrapper">
                    {activeTab !== 'review' && (
                        <>
                            <h1 className="page-title">Onboarding Digital Form</h1>
                            <div className="tabs">
                                {/* Form 2 */}
                                <button
                                    className={`tab ${activeTab === 'form2' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('form2')}
                                >
                                    Form 2 {completedForms.form2 && <span className="tab-checkmark">✓</span>}
                                </button>

                                {/* Gratuity */}
                                <button
                                    className={`tab ${activeTab === 'gratuity' ? 'active' : ''} ${!completedForms.form2 ? 'disabled' : ''}`}
                                    onClick={() => handleTabClick('gratuity')}
                                    disabled={!completedForms.form2}
                                >
                                    Gratuity Nomination {completedForms.gratuity && <span className="tab-checkmark">✓</span>}
                                </button>

                                {/* Form 11 */}
                                <button
                                    className={`tab ${activeTab === 'form11' ? 'active' : ''} ${!completedForms.form2 || !completedForms.gratuity ? 'disabled' : ''}`}
                                    onClick={() => handleTabClick('form11')}
                                    disabled={!completedForms.form2 || !completedForms.gratuity}
                                >
                                    Form 11 {completedForms.form11 && <span className="tab-checkmark">✓</span>}
                                </button>

                                {/* Form L — ONLY for female employees ──────── */}
                                {isFemale && (
                                    <button
                                        className={`tab ${activeTab === 'formL' ? 'active' : ''} ${!completedForms.form11 ? 'disabled' : ''}`}
                                        onClick={() => handleTabClick('formL')}
                                        disabled={!completedForms.form11}
                                    >
                                        Form L {completedForms.formL && <span className="tab-checkmark">✓</span>}
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === 'form2' && (
                        <Form2
                            ref={form2Ref}
                            onContinue={handleForm2Continue}
                            onDraftSave={handleForm2Draft}
                            initialData={form2Data}
                            initialNominees={form2Nominees}
                            fieldEditability={fieldEditability}
                        />
                    )}
                    {activeTab === 'gratuity' && (
                        <GratuityNomination
                            ref={gratuityRef}
                            onContinue={handleGratuityContinue}
                            onDraftSave={handleGratuityDraft}
                            onBack={handleBackToForm2}
                            initialData={gratuityData}
                            initialNominees={gratuityNominees}
                            fieldEditability={fieldEditability}
                            autoSignature={autoSignature}
                        />
                    )}
                    {activeTab === 'form11' && (
                        <Form11
                            ref={form11Ref}
                            onSubmit={handleForm11Submit}
                            onDraftSave={handleForm11Draft}
                            onBack={handleBackToGratuity}
                            initialData={form11Data}
                            fieldEditability={fieldEditability}
                            autoSignature={autoSignature}
                            isFemale={isFemale}
                        />
                    )}

                    {/* Form L — only renders for female employees ─────────── */}
                    {activeTab === 'formL' && isFemale && (
                        <FormL
                            ref={formLRef}
                            onSubmit={handleFormLSubmit}
                            onDraftSave={handleFormLDraft}
                            onBack={handleBackToForm11}
                            initialData={formLData}
                            autoSignature={autoSignature}
                        />
                    )}

                    {activeTab === 'review' && (
                        <Review
                            form2Data={form2Data}
                            gratuityData={gratuityData}
                            form11Data={form11Data}
                            form2Nominees={form2Nominees}
                            gratuityNominees={gratuityNominees}
                            formLData={isFemale ? formLData : undefined}
                            isFemale={isFemale}
                            onBack={isFemale ? handleBackToFormL : handleBackToForm11}
                            onSubmitSuccess={handleFinalSubmitSuccess}
                        />
                    )}
                </div>
            </main>
            <FooterSection />

            <Dialog
                visible={showWarningDialog}
                onHide={() => { }}
                header={
                    <div className="dialog-header warning-header">
                        <div className="warning-icon-large">!</div>
                        <h3>Warning</h3>
                    </div>
                }
                modal closable={false} className="warning-dialog" style={{ width: '450px' }}
            >
                <div className="dialog-content">
                    <p className="warning-message">
                        All information will be submitted to the government portal. Please ensure accuracy before proceeding!
                    </p>
                    <Button label="Continue" onClick={handleWarningContinue} className="warning-continue-btn" />
                </div>
            </Dialog>
        </div>
    );
};

export default MainPage;
